import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import VatAdvancesDataService from '../../../../Services/vatAdvancesData.service';
import './StatusOverviewChart.css';

const PERIOD_OPTIONS = [3, 6, 12];

const formatAmount = (value, beautifyNumber) => {
  const n = Number(value) || 0;
  if (typeof beautifyNumber === 'function') {
    return `${beautifyNumber(n)} ₪`;
  }
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} ₪`;
};

/**
 * CSS bar chart matching html-preview/xtram_dashboard chart panel.
 * Loads monthly incomes / expenses / profit from GetStatusOverviewChartData.
 */
const StatusOverviewChart = ({
  caseId,
  accountantId,
  token,
  startDate,
  endDate,
  onlyProcessed = false,
  fallbackIncomes = 0,
  fallbackExpenses = 0,
  fallbackProfit = 0,
  fallbackLabel = '',
  beautifyNumber,
  t,
}) => {
  const [monthCount, setMonthCount] = useState(6);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const menuRef = useRef(null);
  const requestIdRef = useRef(0);

  const toUtcMonthIso = (value) => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString();
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  useEffect(() => {
    if (!caseId || !token || !startDate) {
      setMonths([{
        label: fallbackLabel || '—',
        incomesAmount: Number(fallbackIncomes) || 0,
        expensesAmount: Number(fallbackExpenses) || 0,
        profitAmount: Number(fallbackProfit) || 0,
      }]);
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const data = await VatAdvancesDataService.fetchStatusOverviewChartData({
          CaseId: caseId,
          StartDate: toUtcMonthIso(startDate),
          EndDate: toUtcMonthIso(endDate) || undefined,
          MonthCount: monthCount,
          AccountantId: accountantId || undefined,
          OnlyProcessed: !!onlyProcessed,
        }, token);

        if (cancelled || requestId !== requestIdRef.current) return;

        const points = (data?.months || data?.Months || []).map((m) => ({
          label: m.label || m.Label || '—',
          incomesAmount: Number(m.incomesAmount ?? m.IncomesAmount) || 0,
          expensesAmount: Number(m.expensesAmount ?? m.ExpensesAmount) || 0,
          profitAmount: Number(m.profitAmount ?? m.ProfitAmount) || 0,
        }));

        setMonths(points.length > 0 ? points : [{
          label: fallbackLabel || '—',
          incomesAmount: Number(fallbackIncomes) || 0,
          expensesAmount: Number(fallbackExpenses) || 0,
          profitAmount: Number(fallbackProfit) || 0,
        }]);
      } catch (err) {
        console.warn('Income overview chart fetch failed', err);
        if (cancelled || requestId !== requestIdRef.current) return;
        setMonths([{
          label: fallbackLabel || '—',
          incomesAmount: Number(fallbackIncomes) || 0,
          expensesAmount: Number(fallbackExpenses) || 0,
          profitAmount: Number(fallbackProfit) || 0,
        }]);
      } finally {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [
    caseId,
    accountantId,
    token,
    startDate,
    endDate,
    monthCount,
    onlyProcessed,
    fallbackIncomes,
    fallbackExpenses,
    fallbackProfit,
    fallbackLabel,
  ]);

  const { yTicks, scaledMonths } = useMemo(() => {
    const peak = Math.max(
      1,
      ...months.flatMap((m) => [
        Math.max(0, m.incomesAmount),
        Math.max(0, m.expensesAmount),
        Math.max(0, m.profitAmount),
      ])
    );
    const niceMax = Math.ceil(peak / 1000) * 1000 || 1000;
    const toPct = (v) => `${Math.max(v > 0 ? 4 : 0, Math.round((Math.max(0, v) / niceMax) * 100))}%`;

    const formatTick = (v) => {
      if (v >= 1000) return `${Math.round(v / 1000)}K`;
      return String(Math.round(v));
    };

    return {
      yTicks: [niceMax, niceMax * 0.8, niceMax * 0.6, niceMax * 0.4, niceMax * 0.2, 0].map(formatTick),
      scaledMonths: months.map((m) => ({
        ...m,
        incomesH: toPct(m.incomesAmount),
        expensesH: toPct(m.expensesAmount),
        profitH: toPct(m.profitAmount),
      })),
    };
  }, [months]);

  const periodLabel = t('StatusOverviewMonths', { count: monthCount });
  const hoveredMonth = hoveredIndex != null ? scaledMonths[hoveredIndex] : null;
  const title = t('StatusOverview') || 'סקירת מצב';

  return (
    <div className={`va-chart ${loading ? 'is-loading' : ''}`.trim()}>
      <div className="va-chart__header">
        <div className="va-chart__heading">
          <h2 className="va-chart__title">{title}</h2>
          <div className="va-chart__legend" aria-hidden="true">
            <span><i className="va-chart__dot va-chart__dot--blue" />{t('Incomes')}</span>
            <span><i className="va-chart__dot va-chart__dot--orange" />{t('Expenses')}</span>
            <span><i className="va-chart__dot va-chart__dot--teal" />{t('financialProfit')}</span>
          </div>
        </div>
        <div className="va-chart__period" ref={menuRef}>
          <button
            type="button"
            className="va-chart__period-btn"
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
          >
            {periodLabel}
            <span aria-hidden="true">⌄</span>
          </button>
          {menuOpen && (
            <div className="va-chart__period-menu" role="menu">
              {PERIOD_OPTIONS.map((count) => (
                <button
                  key={count}
                  type="button"
                  role="menuitem"
                  className={count === monthCount ? 'is-active' : ''}
                  onClick={() => {
                    setMonthCount(count);
                    setMenuOpen(false);
                  }}
                >
                  {t('StatusOverviewMonths', { count })}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div
        className="va-chart__area"
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <div className="va-chart__y">
          {yTicks.map((tick, idx) => (
            <span key={`${tick}-${idx}`}>{tick}</span>
          ))}
        </div>
        <div
          className="va-chart__plot"
          style={{ gridTemplateColumns: `repeat(${Math.max(1, scaledMonths.length)}, minmax(0, 1fr))` }}
        >
          <div className="va-chart__grid l1" />
          <div className="va-chart__grid l2" />
          <div className="va-chart__grid l3" />
          <div className="va-chart__grid l4" />
          <div className="va-chart__grid l5" />
          {scaledMonths.map((m, index) => (
            <div
              className={`va-chart__month ${hoveredIndex === index ? 'is-hovered' : ''}`.trim()}
              key={m.label}
              onMouseEnter={() => setHoveredIndex(index)}
            >
              <div className="va-chart__bars">
                <i className="va-chart__bar va-chart__bar--blue" style={{ '--h': m.incomesH }} />
                <i className="va-chart__bar va-chart__bar--orange" style={{ '--h': m.expensesH }} />
                <i className="va-chart__bar va-chart__bar--teal" style={{ '--h': m.profitH }} />
              </div>
              <span>{m.label}</span>

              {hoveredIndex === index && (
                <div
                  className={`va-chart__tooltip ${index >= scaledMonths.length - 2 ? 'is-start' : 'is-end'}`.trim()}
                  role="tooltip"
                >
                  <strong className="va-chart__tooltip-title">{m.label}</strong>
                  <div className="va-chart__tooltip-row">
                    <i className="va-chart__dot va-chart__dot--blue" />
                    <span>{t('Incomes')}</span>
                    <b>{formatAmount(m.incomesAmount, beautifyNumber)}</b>
                  </div>
                  <div className="va-chart__tooltip-row">
                    <i className="va-chart__dot va-chart__dot--orange" />
                    <span>{t('Expenses')}</span>
                    <b>{formatAmount(m.expensesAmount, beautifyNumber)}</b>
                  </div>
                  <div className="va-chart__tooltip-row">
                    <i className="va-chart__dot va-chart__dot--teal" />
                    <span>{t('financialProfit')}</span>
                    <b>{formatAmount(m.profitAmount, beautifyNumber)}</b>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {hoveredMonth && (
        <span className="va-chart__sr-only">
          {hoveredMonth.label}: {t('Incomes')} {formatAmount(hoveredMonth.incomesAmount, beautifyNumber)},
          {' '}{t('Expenses')} {formatAmount(hoveredMonth.expensesAmount, beautifyNumber)},
          {' '}{t('financialProfit')} {formatAmount(hoveredMonth.profitAmount, beautifyNumber)}
        </span>
      )}
    </div>
  );
};

StatusOverviewChart.propTypes = {
  caseId: PropTypes.string,
  accountantId: PropTypes.string,
  token: PropTypes.string,
  startDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  endDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onlyProcessed: PropTypes.bool,
  fallbackIncomes: PropTypes.number,
  fallbackExpenses: PropTypes.number,
  fallbackProfit: PropTypes.number,
  fallbackLabel: PropTypes.string,
  beautifyNumber: PropTypes.func,
  t: PropTypes.func.isRequired,
};

export default StatusOverviewChart;
